/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  ChangeLlmModelStatusPathParameters,
  ListLlmModelsParams,
  TLHTTPValidationError,
  TLLlmModelCreateRequest,
  TLLlmModelListResponse,
  TLLlmModelResponse,
  TLLlmModelStatusUpdateRequest,
  TLLlmModelUpdateRequest,
  UpdateLlmModelPathParameters,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary List Llm Models
 */
export const listLlmModels = (
  params?: ListLlmModelsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLLlmModelListResponse>({ url: `/llm-models`, method: 'GET', params, signal }, options);
};

export const getListLlmModelsQueryKey = (params?: ListLlmModelsParams) => {
  return ['llm-models', ...(params ? [params] : [])] as const;
};

export const getListLlmModelsQueryOptions = <
  TData = Awaited<ReturnType<typeof listLlmModels>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListLlmModelsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listLlmModels>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListLlmModelsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listLlmModels>>> = ({ signal }) =>
    listLlmModels(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listLlmModels>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListLlmModelsQueryResult = NonNullable<Awaited<ReturnType<typeof listLlmModels>>>;
export type ListLlmModelsQueryError = ErrorType<TLHTTPValidationError>;

export function useListLlmModels<
  TData = Awaited<ReturnType<typeof listLlmModels>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | ListLlmModelsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listLlmModels>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listLlmModels>>,
          TError,
          Awaited<ReturnType<typeof listLlmModels>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListLlmModels<
  TData = Awaited<ReturnType<typeof listLlmModels>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListLlmModelsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listLlmModels>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listLlmModels>>,
          TError,
          Awaited<ReturnType<typeof listLlmModels>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListLlmModels<
  TData = Awaited<ReturnType<typeof listLlmModels>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListLlmModelsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listLlmModels>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Llm Models
 */

export function useListLlmModels<
  TData = Awaited<ReturnType<typeof listLlmModels>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListLlmModelsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listLlmModels>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListLlmModelsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List Llm Models
 */
export const prefetchListLlmModelsQuery = async <
  TData = Awaited<ReturnType<typeof listLlmModels>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: ListLlmModelsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listLlmModels>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListLlmModelsQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List Llm Models
 */
export const invalidateListLlmModels = async (
  queryClient: QueryClient,
  params?: ListLlmModelsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getListLlmModelsQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary Create Llm Model
 */
export const createLlmModel = (
  tLLlmModelCreateRequest: BodyType<TLLlmModelCreateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLLlmModelResponse>(
    {
      url: `/llm-models`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLLlmModelCreateRequest,
      signal,
    },
    options,
  );
};

export const getCreateLlmModelQueryKey = (tLLlmModelCreateRequest?: BodyType<TLLlmModelCreateRequest>) => {
  return ['POST', 'llm-models', tLLlmModelCreateRequest] as const;
};

export const getCreateLlmModelQueryOptions = <
  TData = Awaited<ReturnType<typeof createLlmModel>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLLlmModelCreateRequest: BodyType<TLLlmModelCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createLlmModel>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getCreateLlmModelQueryKey(tLLlmModelCreateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof createLlmModel>>> = ({ signal }) =>
    createLlmModel(tLLlmModelCreateRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof createLlmModel>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type CreateLlmModelQueryResult = NonNullable<Awaited<ReturnType<typeof createLlmModel>>>;
export type CreateLlmModelQueryError = ErrorType<TLHTTPValidationError>;

export function useCreateLlmModel<
  TData = Awaited<ReturnType<typeof createLlmModel>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLLlmModelCreateRequest: BodyType<TLLlmModelCreateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof createLlmModel>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof createLlmModel>>,
          TError,
          Awaited<ReturnType<typeof createLlmModel>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateLlmModel<
  TData = Awaited<ReturnType<typeof createLlmModel>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLLlmModelCreateRequest: BodyType<TLLlmModelCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createLlmModel>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof createLlmModel>>,
          TError,
          Awaited<ReturnType<typeof createLlmModel>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateLlmModel<
  TData = Awaited<ReturnType<typeof createLlmModel>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLLlmModelCreateRequest: BodyType<TLLlmModelCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createLlmModel>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create Llm Model
 */

export function useCreateLlmModel<
  TData = Awaited<ReturnType<typeof createLlmModel>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLLlmModelCreateRequest: BodyType<TLLlmModelCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createLlmModel>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCreateLlmModelQueryOptions(tLLlmModelCreateRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create Llm Model
 */
export const prefetchCreateLlmModelQuery = async <
  TData = Awaited<ReturnType<typeof createLlmModel>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLLlmModelCreateRequest: BodyType<TLLlmModelCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createLlmModel>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCreateLlmModelQueryOptions(tLLlmModelCreateRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Create Llm Model
 */
export const invalidateCreateLlmModel = async (
  queryClient: QueryClient,
  tLLlmModelCreateRequest: BodyType<TLLlmModelCreateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getCreateLlmModelQueryKey(tLLlmModelCreateRequest) }, options);

  return queryClient;
};

/**
 * @summary Update Llm Model
 */
export const updateLlmModel = (
  { modelId }: UpdateLlmModelPathParameters,
  tLLlmModelUpdateRequest: BodyType<TLLlmModelUpdateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLLlmModelResponse>(
    {
      url: `/llm-models/${modelId}`,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      data: tLLlmModelUpdateRequest,
      signal,
    },
    options,
  );
};

export const getUpdateLlmModelQueryKey = (
  { modelId }: UpdateLlmModelPathParameters,
  tLLlmModelUpdateRequest?: BodyType<TLLlmModelUpdateRequest>,
) => {
  return ['PUT', 'llm-models', modelId, tLLlmModelUpdateRequest] as const;
};

export const getUpdateLlmModelQueryOptions = <
  TData = Awaited<ReturnType<typeof updateLlmModel>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { modelId }: UpdateLlmModelPathParameters,
  tLLlmModelUpdateRequest: BodyType<TLLlmModelUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateLlmModel>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getUpdateLlmModelQueryKey({ modelId }, tLLlmModelUpdateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof updateLlmModel>>> = ({ signal }) =>
    updateLlmModel({ modelId }, tLLlmModelUpdateRequest, requestOptions, signal);

  return { queryKey, queryFn, enabled: modelId !== null && modelId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof updateLlmModel>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type UpdateLlmModelQueryResult = NonNullable<Awaited<ReturnType<typeof updateLlmModel>>>;
export type UpdateLlmModelQueryError = ErrorType<TLHTTPValidationError>;

export function useUpdateLlmModel<
  TData = Awaited<ReturnType<typeof updateLlmModel>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateLlmModelPathParameters,
  tLLlmModelUpdateRequest: BodyType<TLLlmModelUpdateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateLlmModel>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateLlmModel>>,
          TError,
          Awaited<ReturnType<typeof updateLlmModel>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateLlmModel<
  TData = Awaited<ReturnType<typeof updateLlmModel>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateLlmModelPathParameters,
  tLLlmModelUpdateRequest: BodyType<TLLlmModelUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateLlmModel>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateLlmModel>>,
          TError,
          Awaited<ReturnType<typeof updateLlmModel>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateLlmModel<
  TData = Awaited<ReturnType<typeof updateLlmModel>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateLlmModelPathParameters,
  tLLlmModelUpdateRequest: BodyType<TLLlmModelUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateLlmModel>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update Llm Model
 */

export function useUpdateLlmModel<
  TData = Awaited<ReturnType<typeof updateLlmModel>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { modelId }: UpdateLlmModelPathParameters,
  tLLlmModelUpdateRequest: BodyType<TLLlmModelUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateLlmModel>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUpdateLlmModelQueryOptions({ modelId }, tLLlmModelUpdateRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Update Llm Model
 */
export const prefetchUpdateLlmModelQuery = async <
  TData = Awaited<ReturnType<typeof updateLlmModel>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { modelId }: UpdateLlmModelPathParameters,
  tLLlmModelUpdateRequest: BodyType<TLLlmModelUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateLlmModel>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUpdateLlmModelQueryOptions({ modelId }, tLLlmModelUpdateRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Update Llm Model
 */
export const invalidateUpdateLlmModel = async (
  queryClient: QueryClient,
  { modelId }: UpdateLlmModelPathParameters,
  tLLlmModelUpdateRequest: BodyType<TLLlmModelUpdateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getUpdateLlmModelQueryKey({ modelId }, tLLlmModelUpdateRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Change Llm Model Status
 */
export const changeLlmModelStatus = (
  { modelId }: ChangeLlmModelStatusPathParameters,
  tLLlmModelStatusUpdateRequest: BodyType<TLLlmModelStatusUpdateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLLlmModelResponse>(
    {
      url: `/llm-models/${modelId}/status`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLLlmModelStatusUpdateRequest,
      signal,
    },
    options,
  );
};

export const getChangeLlmModelStatusQueryKey = (
  { modelId }: ChangeLlmModelStatusPathParameters,
  tLLlmModelStatusUpdateRequest?: BodyType<TLLlmModelStatusUpdateRequest>,
) => {
  return ['POST', 'llm-models', modelId, 'status', tLLlmModelStatusUpdateRequest] as const;
};

export const getChangeLlmModelStatusQueryOptions = <
  TData = Awaited<ReturnType<typeof changeLlmModelStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { modelId }: ChangeLlmModelStatusPathParameters,
  tLLlmModelStatusUpdateRequest: BodyType<TLLlmModelStatusUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof changeLlmModelStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getChangeLlmModelStatusQueryKey({ modelId }, tLLlmModelStatusUpdateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof changeLlmModelStatus>>> = ({ signal }) =>
    changeLlmModelStatus({ modelId }, tLLlmModelStatusUpdateRequest, requestOptions, signal);

  return { queryKey, queryFn, enabled: modelId !== null && modelId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof changeLlmModelStatus>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ChangeLlmModelStatusQueryResult = NonNullable<Awaited<ReturnType<typeof changeLlmModelStatus>>>;
export type ChangeLlmModelStatusQueryError = ErrorType<TLHTTPValidationError>;

export function useChangeLlmModelStatus<
  TData = Awaited<ReturnType<typeof changeLlmModelStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ChangeLlmModelStatusPathParameters,
  tLLlmModelStatusUpdateRequest: BodyType<TLLlmModelStatusUpdateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof changeLlmModelStatus>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof changeLlmModelStatus>>,
          TError,
          Awaited<ReturnType<typeof changeLlmModelStatus>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useChangeLlmModelStatus<
  TData = Awaited<ReturnType<typeof changeLlmModelStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ChangeLlmModelStatusPathParameters,
  tLLlmModelStatusUpdateRequest: BodyType<TLLlmModelStatusUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof changeLlmModelStatus>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof changeLlmModelStatus>>,
          TError,
          Awaited<ReturnType<typeof changeLlmModelStatus>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useChangeLlmModelStatus<
  TData = Awaited<ReturnType<typeof changeLlmModelStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ChangeLlmModelStatusPathParameters,
  tLLlmModelStatusUpdateRequest: BodyType<TLLlmModelStatusUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof changeLlmModelStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Change Llm Model Status
 */

export function useChangeLlmModelStatus<
  TData = Awaited<ReturnType<typeof changeLlmModelStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { modelId }: ChangeLlmModelStatusPathParameters,
  tLLlmModelStatusUpdateRequest: BodyType<TLLlmModelStatusUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof changeLlmModelStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getChangeLlmModelStatusQueryOptions({ modelId }, tLLlmModelStatusUpdateRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Change Llm Model Status
 */
export const prefetchChangeLlmModelStatusQuery = async <
  TData = Awaited<ReturnType<typeof changeLlmModelStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { modelId }: ChangeLlmModelStatusPathParameters,
  tLLlmModelStatusUpdateRequest: BodyType<TLLlmModelStatusUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof changeLlmModelStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getChangeLlmModelStatusQueryOptions({ modelId }, tLLlmModelStatusUpdateRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Change Llm Model Status
 */
export const invalidateChangeLlmModelStatus = async (
  queryClient: QueryClient,
  { modelId }: ChangeLlmModelStatusPathParameters,
  tLLlmModelStatusUpdateRequest: BodyType<TLLlmModelStatusUpdateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getChangeLlmModelStatusQueryKey({ modelId }, tLLlmModelStatusUpdateRequest) },
    options,
  );

  return queryClient;
};
